import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useConsentServiceRecordConsentMutation } from '../api/generated-api';
import { sidekickRtkClient } from '../api/overrides';
import type {
  AcceptSidekickConsent,
  GetSidekickConsentStatus,
} from '../contexts/SidekickApiContext';
import { useIsSidekickLegalConsentApiEnabled } from '../flag';

const ACCEPTED_CONSENT_STATUS = 'ACCEPTED';
const ACCEPT_CONSENT_DECISION = 'ACCEPT';

/**
 * Consent read/write pair, or an empty object when the bridge cannot
 * run. Deliberately all-or-nothing so `useConsentApi` never pairs an
 * API read with a localStorage write.
 */
export type DefaultConsentBridge = {
  getConsentStatus?: GetSidekickConsentStatus;
  acceptConsent?: AcceptSidekickConsent;
};

/**
 * Package-owned default consent bridge, backed by the package's
 * generated `ConsentService` client. Reads the stored ECPP decision and
 * records acceptance against the signed-in user's profile.
 *
 * Returns `{}` while the legal-consent API flag is off or when no
 * `profileId` is available. Yielding instead lets `useConsentApi` preserve
 * the localStorage fallback used by the staged rollout.
 *
 * @param profileId - The customer/user UUID consent is keyed on.
 * @returns The consent callback pair, or `{}` when the API path is unavailable.
 */
export const useDefaultConsentBridge = (
  profileId?: string
): DefaultConsentBridge => {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
  const isConsentApiEnabled = useIsSidekickLegalConsentApiEnabled();
  const [recordConsent] = useConsentServiceRecordConsentMutation();
  const resolvedProfileId =
    profileId === undefined || profileId.trim() === ''
      ? undefined
      : profileId.trim();

  const getConsentStatus = useCallback<GetSidekickConsentStatus>(async () => {
    const query = dispatch(
      sidekickRtkClient.endpoints.consentServiceGetConsent.initiate(
        { profileId: resolvedProfileId },
        { forceRefetch: true }
      )
    );

    try {
      const response = await query.unwrap();
      return {
        hasConsented: response.status === ACCEPTED_CONSENT_STATUS,
        terms: response.currentTerms
          ? {
              code: response.currentTerms.code,
              revision: response.currentTerms.revision,
            }
          : undefined,
        degraded: response.degraded,
      };
    } finally {
      query.unsubscribe();
    }
  }, [dispatch, resolvedProfileId]);

  const acceptConsent = useCallback<AcceptSidekickConsent>(async () => {
    if (resolvedProfileId === undefined) {
      throw new Error('Sidekick consent API requires a signed-in user');
    }
    const response = await recordConsent({
      recordConsentRequest: {
        profileId: resolvedProfileId,
        decision: ACCEPT_CONSENT_DECISION,
      },
    }).unwrap();

    return { persisted: response.persisted, degraded: response.degraded };
  }, [recordConsent, resolvedProfileId]);

  return useMemo(
    () =>
      !isConsentApiEnabled || resolvedProfileId === undefined
        ? {}
        : { getConsentStatus, acceptConsent },
    [acceptConsent, getConsentStatus, isConsentApiEnabled, resolvedProfileId]
  );
};
